/**
 * Intake form field definitions — the canonical shape for every
 * questionnaire field an EDC can build in the form builder.
 *
 * Single source of truth shared across:
 *   - apps/web form builder and renderer
 *   - apps/intake public renderer
 *   - apps/api submission validator (`validateSubmissionData` below)
 *
 * The builder writes these into `intake_forms.fields` (JSONB). The validator
 * enforces the corresponding constraints on every submission before the row
 * lands in `intake_submissions.data`, so downstream promotion flows
 * (mapSubmissionToWizard, report rendering) can trust the shape.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Field definition schemas (the shape stored on intake_forms.fields)
// ─────────────────────────────────────────────────────────────────────────────

export const FIELD_TYPES = ['text', 'number', 'select', 'multiselect', 'file'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

const FieldBase = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  placeholder: z.string().optional(),
  required: z.boolean(),
  helpText: z.string().optional(),
});

export const TextFieldSchema = FieldBase.extend({
  type: z.literal('text'),
});

export const NumberFieldSchema = FieldBase.extend({
  type: z.literal('number'),
  min: z.number().optional(),
  max: z.number().optional(),
});

export const SelectFieldSchema = FieldBase.extend({
  type: z.literal('select'),
  options: z.array(z.string().min(1)).min(1),
});

export const MultiselectFieldSchema = FieldBase.extend({
  type: z.literal('multiselect'),
  options: z.array(z.string().min(1)).min(1),
});

export const FileFieldSchema = FieldBase.extend({
  type: z.literal('file'),
  accept: z.string().optional(),
  maxSizeMb: z.number().positive().optional(),
});

export const FormFieldSchema = z.discriminatedUnion('type', [
  TextFieldSchema,
  NumberFieldSchema,
  SelectFieldSchema,
  MultiselectFieldSchema,
  FileFieldSchema,
]);

/**
 * Strict, validated shape — the output of FormFieldSchema.parse().
 * Submissions are only accepted if the form's stored definition is parseable
 * as ValidFormField[].
 */
export type ValidFormField = z.infer<typeof FormFieldSchema>;

/**
 * Loose authoring-time type. The builder progressively fills a field out —
 * a user can drop a `select` onto the canvas before typing any options —
 * so every discriminator-dependent property is optional here. The strict
 * discriminated union above is what gates submissions.
 */
export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  helpText?: string;
  /** select / multiselect */
  options?: string[];
  /** number */
  min?: number;
  max?: number;
  /** file */
  accept?: string;
  maxSizeMb?: number;
}

export const FormFieldsSchema = z.array(FormFieldSchema);

// ─────────────────────────────────────────────────────────────────────────────
// Submission payload shape (one-per-field value, keyed by field.id)
// ─────────────────────────────────────────────────────────────────────────────

/** Payload contract for a single `file` field reference. */
export const FileSubmissionRefSchema = z.object({
  url: z.string().url().max(2048),
  size: z.number().int().nonnegative(),
  mimeType: z.string().min(1).max(255),
  filename: z.string().min(1).max(512),
});
export type FileSubmissionRef = z.infer<typeof FileSubmissionRefSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Caps — conservative bounds to keep `intake_submissions.data` small and to
// protect downstream consumers from being DoS'd with junk payloads. These are
// deliberately strict; raise per-field if a real form legitimately needs more.
// ─────────────────────────────────────────────────────────────────────────────

export const SUBMISSION_CAPS = {
  /** Max serialized JSON size of the `data` blob. */
  maxJsonBytes: 256 * 1024, // 256 KB
  /** Max characters for any single `text` value. */
  maxTextChars: 5_000,
  /** Max entries selected in a single `multiselect`. */
  maxMultiselectEntries: 50,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Validator
// ─────────────────────────────────────────────────────────────────────────────

export type SubmissionIssueCode =
  | 'required'
  | 'type'
  | 'range'
  | 'not_in_options'
  | 'too_long'
  | 'too_many'
  | 'unknown_field'
  | 'invalid_form_definition'
  | 'payload_too_large'
  | 'file_too_large'
  | 'file_mime_not_allowed'
  | 'file_shape';

export interface SubmissionIssue {
  /** Field id (from the form definition). `_root` for payload-level issues. */
  readonly fieldId: string;
  readonly code: SubmissionIssueCode;
  readonly message: string;
}

export type ValidateSubmissionResult =
  | { readonly ok: true; readonly normalized: Record<string, unknown> }
  | { readonly ok: false; readonly issues: readonly SubmissionIssue[] };

/**
 * Validate and normalize a submission payload against a form's field
 * definitions. Does NOT mutate the input.
 *
 * Contract:
 *   - Every `required` field must be present and non-empty.
 *   - Unknown keys in `data` (no matching field id) are rejected — this
 *     prevents clients from stuffing arbitrary junk into the row.
 *   - `number` accepts numeric strings and coerces. HTML form posts
 *     commonly arrive stringified.
 *   - `file` values must match `FileSubmissionRefSchema` — inline base64 is
 *     not supported (uploads go through a pre-signed URL flow).
 *   - The serialized JSON body is capped at SUBMISSION_CAPS.maxJsonBytes.
 *
 * Returns the normalized/coerced `data` map on success, or an array of
 * per-field issues on failure. Issues accumulate (no short-circuit) so the
 * client can surface every problem in one pass.
 */
export function validateSubmissionData(
  rawFields: unknown,
  data: Record<string, unknown>,
): ValidateSubmissionResult {
  // 1. Parse form fields — defensive against malformed/legacy rows.
  const parsedFields = FormFieldsSchema.safeParse(rawFields);
  if (!parsedFields.success) {
    return {
      ok: false,
      issues: [
        {
          fieldId: '_root',
          code: 'invalid_form_definition',
          message: 'Form field definitions are malformed; submission cannot be validated.',
        },
      ],
    };
  }
  const fields = parsedFields.data;

  // 2. Payload size cap (applied to the input, pre-normalization).
  try {
    const serialized = JSON.stringify(data);
    if (utf8ByteLength(serialized) > SUBMISSION_CAPS.maxJsonBytes) {
      return {
        ok: false,
        issues: [
          {
            fieldId: '_root',
            code: 'payload_too_large',
            message: `Submission exceeds ${SUBMISSION_CAPS.maxJsonBytes} bytes.`,
          },
        ],
      };
    }
  } catch {
    return {
      ok: false,
      issues: [
        { fieldId: '_root', code: 'type', message: 'Submission data is not JSON-serializable.' },
      ],
    };
  }

  // 3. Per-field validation.
  const issues: SubmissionIssue[] = [];
  const normalized: Record<string, unknown> = {};
  const fieldIds = new Set(fields.map((f) => f.id));

  // Unknown-key rejection.
  for (const key of Object.keys(data)) {
    if (!fieldIds.has(key)) {
      issues.push({
        fieldId: key,
        code: 'unknown_field',
        message: `Unknown field "${key}".`,
      });
    }
  }

  for (const field of fields) {
    const raw = data[field.id];
    const present = raw !== undefined && raw !== null && raw !== '';

    if (!present) {
      if (field.required) {
        issues.push({
          fieldId: field.id,
          code: 'required',
          message: `"${field.label}" is required.`,
        });
      }
      continue;
    }

    switch (field.type) {
      case 'text': {
        if (typeof raw !== 'string') {
          issues.push({
            fieldId: field.id,
            code: 'type',
            message: `"${field.label}" must be a string.`,
          });
          break;
        }
        const trimmed = raw.trim();
        if (trimmed.length > SUBMISSION_CAPS.maxTextChars) {
          issues.push({
            fieldId: field.id,
            code: 'too_long',
            message: `"${field.label}" exceeds ${SUBMISSION_CAPS.maxTextChars} characters.`,
          });
          break;
        }
        if (field.required && trimmed === '') {
          issues.push({
            fieldId: field.id,
            code: 'required',
            message: `"${field.label}" is required.`,
          });
          break;
        }
        normalized[field.id] = trimmed;
        break;
      }
      case 'number': {
        let n: number | null = null;
        if (typeof raw === 'number' && Number.isFinite(raw)) n = raw;
        else if (typeof raw === 'string' && raw.trim() !== '') {
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) n = parsed;
        }
        if (n === null) {
          issues.push({
            fieldId: field.id,
            code: 'type',
            message: `"${field.label}" must be a number.`,
          });
          break;
        }
        if (field.min !== undefined && n < field.min) {
          issues.push({
            fieldId: field.id,
            code: 'range',
            message: `"${field.label}" must be ≥ ${field.min}.`,
          });
          break;
        }
        if (field.max !== undefined && n > field.max) {
          issues.push({
            fieldId: field.id,
            code: 'range',
            message: `"${field.label}" must be ≤ ${field.max}.`,
          });
          break;
        }
        normalized[field.id] = n;
        break;
      }
      case 'select': {
        if (typeof raw !== 'string') {
          issues.push({
            fieldId: field.id,
            code: 'type',
            message: `"${field.label}" must be a string.`,
          });
          break;
        }
        if (!field.options.includes(raw)) {
          issues.push({
            fieldId: field.id,
            code: 'not_in_options',
            message: `"${field.label}" must be one of the available options.`,
          });
          break;
        }
        normalized[field.id] = raw;
        break;
      }
      case 'multiselect': {
        if (!Array.isArray(raw)) {
          issues.push({
            fieldId: field.id,
            code: 'type',
            message: `"${field.label}" must be an array.`,
          });
          break;
        }
        if (raw.length > SUBMISSION_CAPS.maxMultiselectEntries) {
          issues.push({
            fieldId: field.id,
            code: 'too_many',
            message: `"${field.label}" exceeds ${SUBMISSION_CAPS.maxMultiselectEntries} selections.`,
          });
          break;
        }
        const bad: unknown[] = [];
        const seen = new Set<string>();
        const deduped: string[] = [];
        for (const item of raw) {
          if (typeof item !== 'string' || !field.options.includes(item)) {
            bad.push(item);
            continue;
          }
          if (!seen.has(item)) {
            seen.add(item);
            deduped.push(item);
          }
        }
        if (bad.length > 0) {
          issues.push({
            fieldId: field.id,
            code: 'not_in_options',
            message: `"${field.label}" contains values not in the allowed options.`,
          });
          break;
        }
        if (field.required && deduped.length === 0) {
          issues.push({
            fieldId: field.id,
            code: 'required',
            message: `"${field.label}" requires at least one selection.`,
          });
          break;
        }
        normalized[field.id] = deduped;
        break;
      }
      case 'file': {
        const parsed = FileSubmissionRefSchema.safeParse(raw);
        if (!parsed.success) {
          issues.push({
            fieldId: field.id,
            code: 'file_shape',
            message: `"${field.label}" must be a file reference (url, size, mimeType, filename).`,
          });
          break;
        }
        const ref = parsed.data;
        if (field.maxSizeMb !== undefined && ref.size > field.maxSizeMb * 1024 * 1024) {
          issues.push({
            fieldId: field.id,
            code: 'file_too_large',
            message: `"${field.label}" exceeds ${field.maxSizeMb} MB.`,
          });
          break;
        }
        if (field.accept && !mimeMatches(ref.mimeType, field.accept)) {
          issues.push({
            fieldId: field.id,
            code: 'file_mime_not_allowed',
            message: `"${field.label}" has a MIME type that is not accepted.`,
          });
          break;
        }
        normalized[field.id] = ref;
        break;
      }
    }
  }

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, normalized };
}

/**
 * Pure-JS UTF-8 byte length. Avoids depending on `Buffer` (node-only) or
 * `TextEncoder` (requires DOM/WebWorker lib in tsconfig) so this module
 * stays runnable under the minimal ES2022 `lib` set used by @develio/shared.
 */
function utf8ByteLength(s: string): number {
  let bytes = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      // high surrogate — pair with the low surrogate, 4 bytes total
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

/**
 * HTML input `accept` semantics: a comma-separated list of MIME types or
 * extensions; wildcards (`image/*`) are supported for MIME.
 * We only match MIME patterns here — extensions (e.g. `.pdf`) are treated as
 * best-effort suffix checks against the filename is NOT performed because
 * the filename is attacker-controlled; the MIME type from the upload layer
 * is the trust anchor.
 */
function mimeMatches(mimeType: string, accept: string): boolean {
  const patterns = accept
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  const mt = mimeType.toLowerCase();
  for (const p of patterns) {
    if (p === mt) return true;
    if (p.endsWith('/*')) {
      const prefix = p.slice(0, -1); // keep trailing slash
      if (mt.startsWith(prefix)) return true;
    }
  }
  return false;
}
